import { useEffect, type ReactNode } from 'react';

// Shared modal/sheet primitive. Replaces the duplicated overlay logic across each modal.
// - Backdrop click closes (unless dismissOnBackdrop is false)
// - Escape key closes
// - Body scroll locked while open
// - `variant="dark"` flips the background/text for the cut timer's navy live screen
// - `variant="bottom-sheet"` slides up from the bottom on mobile (rounded top corners)
export function ModalShell({
  open,
  onClose,
  children,
  dismissOnBackdrop = true,
  variant = 'card',
  size = 'md',
  ariaLabel,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  dismissOnBackdrop?: boolean;
  variant?: 'card' | 'dark' | 'bottom-sheet';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  ariaLabel?: string;
}) {
  // Escape closes
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const sizeClass = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
  }[size];

  const containerClass =
    variant === 'dark'
      ? 'bg-navy-900 text-white shadow-2xl'
      : variant === 'bottom-sheet'
      ? 'bg-surface text-ink-900 rounded-t-3xl shadow-2xl mt-auto'
      : 'bg-surface text-ink-900 rounded-2xl shadow-2xl';

  const wrapperClass =
    variant === 'bottom-sheet'
      ? 'fixed inset-0 z-50 flex flex-col justify-end'
      : 'fixed inset-0 z-50 grid place-items-center p-4';

  return (
    <div
      className={wrapperClass}
      onClick={dismissOnBackdrop ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
      <div
        className={`relative ${sizeClass} w-full max-h-[92dvh] overflow-y-auto ${containerClass} ${
          variant === 'dark' ? 'rounded-t-3xl' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalCloseButton({ onClose, variant = 'card' }: { onClose: () => void; variant?: 'card' | 'dark' }) {
  return (
    <button
      type="button"
      onClick={onClose}
      className={`absolute top-4 right-4 w-9 h-9 rounded-full grid place-items-center transition-colors ${
        variant === 'dark'
          ? 'text-white/70 hover:text-white hover:bg-white/10'
          : 'text-ink-500 hover:text-ink-900 hover:bg-bg-alt'
      }`}
      aria-label="Close"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  );
}
